import React, { useMemo } from 'react';
import { format } from 'date-fns';
import type { TimelineItem, HoverPosition } from '../../../types/timeline';
import useUserTimezone from '../../../hooks/useUserTimezone';

interface TimelineTooltipProps {
  item: TimelineItem;
  position: HoverPosition;
  containerHeight: number;
  isVisible: boolean;
}

const TimelineTooltip: React.FC<TimelineTooltipProps> = ({
  item,
  position,
  containerHeight,
  isVisible
}) => {
  // Use the timezone hook
  const { formatDateOnlyWithTimezone } = useUserTimezone();
  
  // Format date in user's timezone without time
  const formatDate = (date: Date | string): string => {
    return formatDateOnlyWithTimezone(date);
  };
  
  // Truncate long text to prevent oversized tooltips
  const truncateText = (text: string, maxLength: number) => {
    if (!text) return '';
    // Allowing longer text now that we have scrolling
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength - 3) + '...';
  };

  // Calculate optimal tooltip position to avoid going off-screen
  const tooltipPosition = useMemo(() => {
    const TOOLTIP_OFFSET = 20; // Distance from cursor/edge
    const TOOLTIP_HEIGHT = 200; // Max height of tooltip
    const TOOLTIP_WIDTH = 300;  // Max width of tooltip
    const CURSOR_BUFFER = 20;   // Minimum distance from cursor

    // Determine vertical position - prefer above cursor when possible
    let top = 'auto';
    let bottom = 'auto';
    
    // If there's enough space above, place it above the cursor
    if (position.y > TOOLTIP_HEIGHT + TOOLTIP_OFFSET) {
      bottom = `${containerHeight - position.y + CURSOR_BUFFER}px`;
    } else {
      // If not enough space above, place below
      top = `${position.y + CURSOR_BUFFER}px`;
    }

    // Determine horizontal position - avoid being directly under/above cursor
    let left = 'auto';
    let right = 'auto';

    if (position.isNearRightEdge) {
      // If near right edge, align to right with offset
      right = `${CURSOR_BUFFER}px`;
    } else if (position.isNearLeftEdge) {
      // If near left edge, align to left with offset
      left = `${CURSOR_BUFFER}px`;
    } else {
      // In the middle - place tooltip to the right of cursor when possible
      left = `${position.x + CURSOR_BUFFER}px`;
    }

    return { top, bottom, left, right };
  }, [position, containerHeight]);

  // Render appropriate content based on item type
  const renderContent = () => {
    if (item.type === 'workExperience') {
      return (
        <>
          <h3 className="font-bold text-lg mb-1">{item.position} at {item.company}</h3>
          <h4 className="text-md text-gray-700 mb-2">{item.location}</h4>
          <p className="text-xs text-gray-600 mb-2">
            {formatDate(item.startDate)} -
            {item.endDate ? formatDate(item.endDate) : 'Present'}
          </p>
          {item.description && (
            <div className="description text-sm text-gray-800 mb-2 leading-relaxed">
              {truncateText(item.description, 300)}
            </div>
          )}
          <p className="text-xs italic text-blue-600 mt-3 border-t pt-2 border-gray-100">Click to focus timeline on this period</p>
        </>
      );
    } else if (item.type === 'retrospective') {
      return (
        <>
          <h3 className="font-bold text-lg mb-1">{item.title}</h3>
          <h4 className="text-md text-gray-700 mb-2">
            {item.frameworkType && 
              item.frameworkType.split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
            }
          </h4>
          <p className="text-xs text-gray-600 mb-2">
            {formatDate(item.startDate)} -
            {item.endDate ? formatDate(item.endDate) : 'Present'}
          </p>
          {item.description && (
            <div className="description text-sm text-gray-800 leading-relaxed">
              {truncateText(item.description, 200)}
            </div>
          )}
        </>
      );
    } else {
      return (
        <>
          <h3 className="font-bold text-lg mb-2">{item.title}</h3>
          {item.description && (
            <div className="description text-sm text-gray-800 mb-2 leading-relaxed">
              {truncateText(item.description, 200)}
            </div>
          )}
          <p className="text-xs text-gray-600">
            {formatDate(item.startDate)}
            {item.endDate && ` - ${formatDate(item.endDate)}`}
          </p>
        </>
      );
    }
  };

  return (
    <div
      className="absolute z-10 p-4 bg-white rounded-lg shadow-lg text-black"
      style={{
        ...tooltipPosition,
        maxWidth: '350px',
        maxHeight: '300px',
        overflow: 'auto',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? 'visible' : 'hidden',
        pointerEvents: 'none',
        transition: 'opacity 300ms ease-in-out, visibility 300ms ease-in-out',
        wordBreak: 'break-word',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
      }}
      role="tooltip"
    >
      {renderContent()}
    </div>
  );
};

export default TimelineTooltip; 